import {motion, transform, useMotionValue, useScroll, useTransform} from "framer-motion";
import React, {useEffect, useRef, useState} from "react";
import {useMedia} from "../hooks/useMedia";
import {browser, cn, getDecoratedTag, hasSellingPlans, spring} from "../lib/utils";
import {ModalMobile} from "./ModalMobile";
import {NutritionTable} from "./NutritionTable";
import {Button, RadioGroup, RadioGroupItem} from "../components";
import {GiftCardRecipientForm} from "./GiftCardRecipientForm";
import * as Icon from "../icons";
import {Trans, useTranslation} from "react-i18next";

interface HeroProps {
  productJson: RootObject;
  selectedVariant: Variant;
  setSelectedVariant: (variant: Variant) => void;
  setFormData: (data: ProductForm) => void;
  handleFormSubmit: (params: Params) => void;
  formData: ProductForm;
  getFormRef: (ref: React.RefObject<HTMLFormElement>) => void;
}

function Hero({
                productJson,
                selectedVariant,
                setSelectedVariant,
                formData,
                setFormData,
                handleFormSubmit,
                getFormRef,
              }: HeroProps) {
  const {t} = useTranslation();
  const hasNutritionData = !!productJson.metafields.nutrition.energykcal;
  const hasUtensils =
    productJson.metafields.recipe.utensils?.split(",").length > 0
      ? productJson.metafields.recipe.utensils?.split(",").join(", ")
      : false;
  const hasAllergens = productJson.metafields.recipe.allergens;
  const isSubscription = hasSellingPlans(productJson);

  const {scrollY} = useScroll();
  const y = useTransform(scrollY, (v) => v / 10);
  const DONT_SHOW_TAGS = [
    "recipes",
    "retseptid-test",
    "boxes",
    "frontpage",
    "grillmenuu",
    "joulumenuu",
  ];

  const [showNutrition, setShowNutrition] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const [tapPos, setTapPos] = useState({x: 0, y: 0});

  function getTapPosition(ev: MouseEvent) {
    if (!browser.isChrome) return;
    const el = ref.current as HTMLElement;
    const {x, y: yTap} = el.getBoundingClientRect();
    const {clientX, clientY} = ev;
    const tapX = (clientX - x) / el.clientWidth;
    const tapY = (clientY - yTap) / el.clientHeight;
    const xRotation = transform(tapY, [0, 0.5, 1], [1, 0, -1]);
    const yRotation = transform(tapX, [0, 0.5, 1], [-5, 0, 5]);
    setTapPos({x: xRotation, y: yRotation});
  }

  const isMobile = useMedia("(max-width: 768px)", true);
  const animationProps: any = isMobile
    ? {}
    : {
      transition: {...spring.bouncy, stiffness: 350},
      style: {
        backfaceVisibility: "hidden",
        pointerEvents: !showNutrition ? "auto" : "none",
      },
      onTapStart: getTapPosition,
      tabIndex: -1,
      whileTap: {
        rotateY: tapPos.y,
        rotateX: tapPos.x,
        transition: spring.snappy,
      },
      initial: {rotateY: 0},
      animate: {rotateY: !showNutrition ? 0 : 180},
    };

  const opacity = useMotionValue(0);

  // Create a state to hold the selected options
  const [selectedOptions, setSelectedOptions] = useState({
    option1: productJson.variants[0].option1,
    option2: productJson.variants[0].option2,
    option3: productJson.variants[0].option3,
  });

  // Create a function to update the selected variant
  const updateSelectedVariant = () => {
    const foundVariant = productJson.variants.find(
      (variant) =>
        variant.option1 === selectedOptions.option1 &&
        (!variant.option2 || variant.option2 === selectedOptions.option2) &&
        (!variant.option3 || variant.option3 === selectedOptions.option3),
    );

    if (foundVariant) {
      setSelectedVariant(foundVariant);
    }
  };

  // Update the selected variant when any option changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(updateSelectedVariant, [selectedOptions]);

  useEffect(() => {
    setFormData(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Function to generate RadioGroup components
  const generateRadioGroup = (
    option: keyof typeof selectedOptions,
    index: number,
  ) => {
    const uniqueOptions = [
      ...new Set(productJson.variants.map((variant) => variant[option])),
    ];

    if (uniqueOptions.length > 1) {
      // Check if the option title is "People" and translate it to "Portsjoneid"
      const optionTitle = productJson.options[index] === "People" ? "Portsjoneid" : productJson.options[index];

      return (
        <div key={index}>
          <h4 className="mb-2">{optionTitle}</h4>
          <RadioGroup
            defaultValue={selectedOptions[option]}
            className="inline-flex"
            medium={true}
            onValueChange={(value) =>
              setSelectedOptions((prev) => ({...prev, [option]: value}))
            }
          >
            {uniqueOptions.map((value, ind) => {
              // Check if the value matches the pattern "number people" and remove the " people" part for display
              const newValue = /^(\d+)\speople$/.test(value) ? value.split(" ")[0] : value;
              return <RadioGroupItem key={ind} value={value}>{newValue}</RadioGroupItem>;
            })}
          </RadioGroup>
        </div>
      );
    }
  };

  const visibleTags = productJson.collections
    .filter((col) => !DONT_SHOW_TAGS.includes(col.handle))
    .map(getDecoratedTag);

  const formRef = useRef(null);

  useEffect(() => {
    getFormRef(formRef);
  }, [getFormRef]);

  const [isLoading, setIsLoading] = useState(false);

  return (
    <form
      onSubmit={(e) => {
        setIsLoading(true);
        e.preventDefault();
        try {
          handleFormSubmit({ref: "website_hero_buy_button"});
        } catch (error) {
          console.error("Error in handleFormSubmit:", error);
        }
        setIsLoading(false);
      }}
    >
      <input
        type="hidden"
        name="id"
        value={selectedVariant.id}
        disabled={!selectedVariant.available}
      />
      <input
        type="hidden"
        name="quantity"
        value={1}
        min={selectedVariant.quantity_rule.min}
        max={selectedVariant.quantity_rule.max}
        step={selectedVariant.quantity_rule.increment}
      />
      <div
        className={cn(
          "bg-white px-2 md:px-0 md:grid md:items-center md:[grid-template-columns:1fr_minmax(31rem,_1fr)_minmax(49rem,_2fr)_1fr]",
          "2xl:[grid-template-columns:minmax(1rem,_6vw)_30rem_1fr_minmax(1rem,_6vw)]",
        )}
      >
        <div
          className={cn(
            "w-full h-full rounded-3xl md:rounded-none aspect-square md:aspect-auto md:h-[85vh] md:max-w-[100vw] md:min-h-[600px] [grid-area:1/1/2/5] overflow-clip",
            "bg-gradient-to-tr from-green-950/10 via-zinc-200 to-green-950/20",
          )}
        >
          <motion.img
            style={{y, opacity}}
            onLoad={() => opacity.set(1)}
            className="w-full h-full object-cover opacity-0 transition-opacity duration-200"
            src={productJson.featured_image}
            alt={productJson.title}
          />
        </div>
        <div
          className={cn([
            "m-2 my-4 md:mx-4 md:[grid-area:1/2/2/3] md:min-h-[50%] md:grid md:[&>div]:[grid-area:1/1]",
            "transform-gpu [perspective:900px] [transform-style:preserve-3d]",
          ])}
        >
          {hasNutritionData && (
            <motion.div
              transition={{...spring.bouncy, stiffness: 350}}
              style={{
                backfaceVisibility: "hidden",
                pointerEvents: showNutrition ? "auto" : "none",
              }}
              initial={{rotateY: -180}}
              onTapStart={getTapPosition}
              whileTap={{
                rotateY: tapPos.y,
                rotateX: tapPos.x,
                transition: spring.snappy,
              }}
              animate={{rotateY: showNutrition ? 0 : -180}}
              tabIndex={-1}
              className="hidden md:flex flex-col gap-2 h-full bg-white z-10 pt-9 pb-8 rounded-3xl md:shadow-2xl md:shadow-black/10"
            >
              <div className="mx-8">
                <h1>{t("recipe_details.nutrition_table.nutritional_value")}</h1>
                <button
                  className="p-4 absolute right-5 top-5 rounded-xl opacity-50 hover:opacity-100 hover:bg-slate-500/5"
                  onClick={(event) => {
                    event.preventDefault();
                    setShowNutrition(false);
                  }}
                >
                  <Icon.Close />
                </button>
              </div>
              <NutritionTable metafields={productJson.metafields} />
            </motion.div>
          )}
          <motion.div
            ref={ref}
            {...animationProps}
            className="md:bg-white md:z-10 md:px-8 md:pt-7 md:pb-8 md:rounded-3xl flex flex-col gap-1 md:gap-3 md:shadow-2xl md:shadow-black/10"
          >
            {" "}
            {hasNutritionData && (
              <div className="flex items-center gap-4 text-green-900">
                <div className="flex items-center gap-1 [&>svg]:w-3 [word-spacing:-1px]">
                  <Icon.Clock />
                  <span className="text-[10px] uppercase tracking-wider">
                    {productJson.metafields.recipe.preparationtime} min
                  </span>
                </div>
                <div className="flex items-center gap-1 [&>svg]:w-3 [&>svg]:-mr-[2px]">
                  <Icon.Fire />
                  <span className="text-[10px] uppercase tracking-wider [word-spacing:-1px]">
                    {productJson.metafields.nutritionpp.energykcal} kcal
                  </span>
                </div>

                <button
                  className={cn(
                    "ml-auto text-green-900 text-[10px] uppercase tracking-wider cursor-pointer px-2 py-1 rounded-full",
                    "max-md:bg-green-900/[0.05] md:hover:bg-green-900/[0.05] active:bg-green-900/[0.1]",
                  )}
                  onClick={(event) => {
                    event.preventDefault();
                    setShowNutrition(true);
                  }}
                >
                  {t("recipe_details.nutrition_table.nutritional_value")}
                </button>
              </div>
            )}
            <h1>{productJson.title}</h1>
            <div
              className={`flex flex-col flex-1 border-zinc-200 ${
                isSubscription
                  ? "md:py-4 md:border-y gap-2"
                  : "md:pt-4 md:border-t gap-4"
              }`}
            >
              {visibleTags && visibleTags.length > 0 && (
                <div className="flex gap-1 flex-wrap -ml-1 saturate-[0.65]">
                  {visibleTags.map(
                    ({id, title, classNames, Icon: LoopIcon, url}) => (
                      <a
                        key={id}
                        href={url}
                        className={`flex items-center [&>svg]:w-3 gap-[2px] h-5 text-[10px] uppercase tracking-wide cursor-pointer px-2 rounded-3xl leading-tight ${classNames}`}
                      >
                        {LoopIcon ? <LoopIcon /> : null}
                        {title}
                      </a>
                    ),
                  )}
                </div>
              )}
              <p
                className="text-gray-700 text-sm tracking-tight"
                dangerouslySetInnerHTML={{__html: productJson.description}}
              />
              {hasUtensils && (
                <p className="text-gray-700 text-sm tracking-tight">
                  <span>{t("recipe_details.utensils")}</span>{" "}
                  {productJson.metafields.recipe.utensils
                    ?.split(",")
                    .join(", ")}
                </p>
              )}
              {!isSubscription && (
                <>
                  {productJson.variants.length > 1 && (
                    <div>
                      {generateRadioGroup("option1", 0)}
                      {generateRadioGroup("option2", 1)}
                      {generateRadioGroup("option3", 2)}
                    </div>
                  )}
                  {productJson.gift_card && (
                    <GiftCardRecipientForm
                      formData={formData}
                      setFormData={setFormData}
                    />
                  )}
                  <div className="flex items-center justify-between">
                    <p className="text-lg text-right flex-grow pr-4">
                      <Trans
                        i18nKey="recipe_details.gift_card.price"
                        values={{price: parseFloat((selectedVariant.price / 100).toFixed(2))}}
                        components={{strong: <strong />}}
                      />
                    </p>
                    <Button
                      ref={formRef}
                      className="max-sm:px-6 max-sm:w-full md:px-8 !rounded-full"
                      disabled={isLoading}
                    >
                      {isLoading ? t("recipe_details.gift_card.button_loading") : t("recipe_details.gift_card.button")}
                    </Button>
                  </div>
                </>
              )}
            </div>
            {hasAllergens && (
              <div className="flex justify-between gap-4 items-end">
                <small className="text-[10px] text-zinc-400 leading-tight [text-wrap:balance]">
                  {t("recipe_details.allergens")} {productJson.metafields.recipe.allergens}
                </small>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      {isMobile && hasNutritionData && (
        <ModalMobile
          title={t("recipe_details.nutrition_table.nutritional_value")}
          onClose={() => setShowNutrition(false)}
          open={showNutrition}
        >
          <NutritionTable metafields={productJson.metafields} />
        </ModalMobile>
      )}
    </form>
  );
}

export {Hero};
